import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ProgressIndicator from '../../../components/ui/ProgressIndicator';
import Button from '../../../components/ui/Button';

const ProgressSummaryCard = ({
  portfolioProgress = 0,
  missingElements = [],
  completedSections = 0,
  totalSections = 0,
  uploadedDocuments = 0
}) => {
  const navigate = useNavigate();

  const getProgressStatus = () => {
    if (portfolioProgress >= 90) return { text: 'Excellent', color: 'text-success', icon: 'CheckCircle2' };
    if (portfolioProgress >= 70) return { text: 'Good Progress', color: 'text-accent', icon: 'TrendingUp' };
    if (portfolioProgress >= 40) return { text: 'Keep Going', color: 'text-warning', icon: 'Activity' };
    return { text: 'Getting Started', color: 'text-muted-foreground', icon: 'Target' };
  };

  const status = getProgressStatus();

  const handleCompleteMissing = (sectionKey) => {
    navigate('/portfolio-builder', { state: { activeSection: sectionKey } });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      {/* Header with Status */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground mb-1">Portfolio Progress</h2>
          <p className="text-sm text-muted-foreground">Complete your profile to unlock opportunities</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted ${status?.color}`}>
          <Icon name={status?.icon} size={16} />
          <span className="text-sm font-medium">{status?.text}</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <ProgressIndicator
          progress={portfolioProgress}
          label="Overall Completion"
          showPercentage={true}
          size="lg"
        />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6 p-4 bg-muted/30 rounded-lg">
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">{completedSections}/{totalSections}</p>
          <p className="text-xs text-muted-foreground">Sections Done</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">{uploadedDocuments}</p>
          <p className="text-xs text-muted-foreground">Documents</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">{portfolioProgress}%</p>
          <p className="text-xs text-muted-foreground">Complete</p>
        </div>
      </div>

      {/* Missing Elements */}
      {missingElements?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="AlertCircle" size={16} className="text-warning" />
            Missing Elements ({missingElements?.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {missingElements?.map((element, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-2 p-3 bg-muted/50 rounded-md border border-border hover:bg-muted transition-smooth cursor-pointer group"
                onClick={() => handleCompleteMissing(element?.sectionKey)}
              >
                <div className="flex items-center gap-2">
                  <Icon name={element?.icon} size={18} className="text-muted-foreground group-hover:text-foreground" />
                  <span className="text-sm text-foreground">{element?.label}</span>
                </div>
                <Icon name="ArrowRight" size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA Button */}
      {portfolioProgress < 100 && (
        <div className="mt-6 pt-6 border-t border-border">
          <Button
            onClick={() => navigate('/portfolio-builder')}
            className="w-full"
            iconName="FileText"
            iconPosition="right"
          >
            Continue Building Portfolio
          </Button>
        </div>
      )}

      {portfolioProgress === 100 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg">
            <Icon name="CheckCircle2" size={20} className="text-success" />
            <div>
              <p className="text-sm font-semibold text-success">Portfolio Complete!</p>
              <p className="text-xs text-muted-foreground">Ready for university applications</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressSummaryCard;